const PLAYER_NAME_KEY = "bingo_player_name";
const CLIENT_ID_KEY = "bingo_client_id";
const PLAYER_ID_PREFIX = "bingo_player_id_";

// Generate a browser fingerprint based on multiple stable characteristics
// This creates a consistent ID even if localStorage is cleared
async function generateBrowserFingerprint(): Promise<string> {
  const components: string[] = [];

  // 1. Screen properties (stable)
  const scr = window.screen;
  components.push(`screen:${scr.width}x${scr.height}x${scr.colorDepth}`);
  components.push(`avail:${scr.availWidth}x${scr.availHeight}`);
  components.push(`pixel:${window.devicePixelRatio || 1}`);

  // 2. Timezone
  components.push(`tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
  components.push(`tzoff:${new Date().getTimezoneOffset()}`);

  // 3. Language settings
  components.push(`lang:${navigator.language}`);
  components.push(`langs:${navigator.languages?.join(",") || ""}`);

  // 4. Platform info
  components.push(`platform:${navigator.platform}`);
  components.push(`cores:${navigator.hardwareConcurrency || 0}`);
  components.push(`memory:${(navigator as any).deviceMemory || 0}`);

  // 5. Touch support
  components.push(`touch:${navigator.maxTouchPoints || 0}`);

  // 6. WebGL renderer (very unique)
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
      if (debugInfo) {
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        components.push(`gl:${vendor}|${renderer}`);
      }
    }
  } catch {
    components.push("gl:none");
  }

  // 7. Canvas fingerprint
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.textBaseline = "top";
      ctx.font = "14px Arial";
      ctx.fillStyle = "#f60";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.fillText("BingoFingerprint", 2, 15);
      ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
      ctx.fillText("BingoFingerprint", 4, 17);
      components.push(`canvas:${canvas.toDataURL().slice(-50)}`);
    }
  } catch {
    components.push("canvas:none");
  }

  // 8. Audio fingerprint (audio context properties)
  try {
    const audioCtx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    components.push(`audio:${audioCtx.sampleRate}`);
    audioCtx.close();
  } catch {
    components.push("audio:none");
  }

  // Create a hash from all components
  const fingerprintString = components.join("||");
  const hash = await hashString(fingerprintString);

  return hash;
}

// Simple hash function using SubtleCrypto
async function hashString(str: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    // Fallback: simple hash for environments without crypto.subtle
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).padStart(8, "0");
  }
}

// Cache the fingerprint
let cachedFingerprint: string | null = null;

// Get browser fingerprint (cached for performance)
export async function getBrowserFingerprint(): Promise<string> {
  if (cachedFingerprint) {
    return cachedFingerprint;
  }
  cachedFingerprint = await generateBrowserFingerprint();
  return cachedFingerprint;
}

// Generate a unique client ID (persistent across sessions)
function generateClientId(): string {
  // Use crypto API for secure random UUID
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Get or create a persistent client ID for this browser
// Combines localStorage ID with browser fingerprint for robustness
export function getClientId(): string {
  try {
    let clientId = localStorage.getItem(CLIENT_ID_KEY);
    if (!clientId) {
      clientId = generateClientId();
      localStorage.setItem(CLIENT_ID_KEY, clientId);
    }
    return clientId;
  } catch {
    // If localStorage fails, generate a temporary ID
    return generateClientId();
  }
}

// Get a combined identifier (localStorage ID + fingerprint)
// This is used for banning - fingerprint persists even if localStorage is cleared
export async function getDeviceIdentifier(): Promise<{
  clientId: string;
  fingerprint: string;
}> {
  const clientId = getClientId();
  const fingerprint = await getBrowserFingerprint();
  return { clientId, fingerprint };
}

// Sanitize input: only allow letters, numbers, and spaces
const sanitizeName = (input: string): string => {
  return input.replace(/[^a-zA-Z0-9\s]/g, "").trim();
};

export function getStoredPlayerName(): string | null {
  try {
    const name = localStorage.getItem(PLAYER_NAME_KEY);
    if (!name) return null;
    // Sanitize on retrieval as well for extra safety
    return sanitizeName(name);
  } catch {
    return null;
  }
}

export function setStoredPlayerName(name: string): void {
  try {
    const sanitized = sanitizeName(name);
    if (sanitized) {
      localStorage.setItem(PLAYER_NAME_KEY, sanitized);
    }
  } catch {
    // Ignore errors
  }
}

export function clearStoredPlayerName(): void {
  try {
    localStorage.removeItem(PLAYER_NAME_KEY);
  } catch {
    // Ignore errors
  }
}

// Store player ID for a specific lobby (for reconnecting)
export function getStoredPlayerId(lobbyCode: string): string | null {
  try {
    return localStorage.getItem(PLAYER_ID_PREFIX + lobbyCode);
  } catch {
    return null;
  }
}

export function setStoredPlayerId(lobbyCode: string, playerId: string): void {
  try {
    localStorage.setItem(PLAYER_ID_PREFIX + lobbyCode, playerId);
  } catch {
    // Ignore errors
  }
}

export function clearStoredPlayerId(lobbyCode: string): void {
  try {
    localStorage.removeItem(PLAYER_ID_PREFIX + lobbyCode);
  } catch {
    // Ignore errors
  }
}

// Store current active OBS session (lobbyCode + playerId) so OBS widget can auto-switch
const CURRENT_OBS_SESSION_KEY = "bingo_current_obs_session";

export interface ObsSession {
  lobbyCode: string;
  playerId: string;
  timestamp: number;
}

export function setCurrentObsSession(
  lobbyCode: string,
  playerId: string
): void {
  try {
    const session: ObsSession = { lobbyCode, playerId, timestamp: Date.now() };
    localStorage.setItem(CURRENT_OBS_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Ignore errors
  }
}

export function getCurrentObsSession(): ObsSession | null {
  try {
    const data = localStorage.getItem(CURRENT_OBS_SESSION_KEY);
    if (!data) return null;
    return JSON.parse(data) as ObsSession;
  } catch {
    return null;
  }
}

export function clearCurrentObsSession(): void {
  try {
    localStorage.removeItem(CURRENT_OBS_SESSION_KEY);
  } catch {
    // Ignore errors
  }
}

// Store OBS settings in localStorage (as a template for new lobbies)
const OBS_SETTINGS_KEY = "bingo_obs_settings";

export interface StoredObsSettings {
  bgOpacity: number;
  bgColor: string;
  borderRadius: number;
  textColor: string;
  borderColor: string;
  winningColor: string;
  fontSize: number;
  fontWeight: number;
}

export function getStoredObsSettings(): StoredObsSettings | null {
  try {
    const data = localStorage.getItem(OBS_SETTINGS_KEY);
    if (!data) return null;
    return JSON.parse(data) as StoredObsSettings;
  } catch {
    return null;
  }
}

export function setStoredObsSettings(settings: StoredObsSettings): void {
  try {
    localStorage.setItem(OBS_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Ignore errors
  }
}

export function clearStoredObsSettings(): void {
  try {
    localStorage.removeItem(OBS_SETTINGS_KEY);
  } catch {
    // Ignore errors
  }
}
